import React, { useEffect, useState, useMemo, useCallback } from "react";
import usePrefectureHook from "./usePrefectureHook";
import { useParams } from "react-router-dom";
import AkiyaCard from "../KominkaAkiyaCard/AkiyaCard";
import SortDropDown from "./SortDropDown"; // Using enhanced component
import MapComponent from "./Maps";
import Loader from "../Loader/Loader";
import Pagination from "./Pagination"; // Using enhanced pagination

const Kominka = () => {
  const { akiyaData, sortDropDownOptions, sortingType, handleFavoriteClick } = usePrefectureHook();
  const { prefecture_name } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [viewData, setViewData] = useState([]);
  const [sortBy, setSortBy] = useState("Sort By");
  const [orderBy, setOrderBy] = useState("Order By");
  const [isLoading, setIsLoading] = useState(false);

  const handleSortbyChange = useCallback((item) => {
    setSortBy(item.display);
    setCurrentPage(1);
  }, []);

  const handleOrderByChange = useCallback((item) => {
    setOrderBy(item.display);
    setCurrentPage(1);
  }, []);

  // Enhanced extraction functions with better error handling
  const extractPriceValue = useCallback((priceString) => {
    if (!priceString || typeof priceString !== "string") {
      return 0;
    }
    
    const cleanPrice = priceString.toLowerCase().trim();
    
    // Handle million/billion values
    if (cleanPrice.includes('million') || cleanPrice.includes('百万')) {
      const number = parseFloat(cleanPrice.replace(/[^\d.]/g, ''));
      return isNaN(number) ? 0 : number * 1_000_000;
    }
    
    if (cleanPrice.includes('billion') || cleanPrice.includes('十億')) {
      const number = parseFloat(cleanPrice.replace(/[^\d.]/g, ''));
      return isNaN(number) ? 0 : number * 1_000_000_000;
    }
    
    const numericValue = parseFloat(cleanPrice.replace(/[^\d.-]/g, ""));
    return isNaN(numericValue) ? 0 : numericValue;
  }, []);

  const extractArea = useCallback((area) => {
    if (!area || typeof area !== "string") {
      return 0;
    }
    const match = area.match(/[\d,.]+/);
    if (!match) return 0;
    const number = parseFloat(match[0].replace(/,/g, ''));
    return isNaN(number) ? 0 : number;
  }, []);

  const extractFloorArea = useCallback((area) => {
    if (!area || typeof area !== "string") {
      return 0;
    }
    const match = area.match(/[\d,.]+/);
    if (!match) return 0;
    const number = parseFloat(match[0].replace(/,/g, ''));
    return isNaN(number) ? 0 : number;
  }, []);

  const extractYear = useCallback((built) => {
    if (!built || typeof built !== "string") {
      return 0;
    }
    const match = built.match(/\d{4}/); // Look for 4-digit year
    return match ? parseInt(match[0]) : 0;
  }, []);

  // Enhanced sorting with better error handling
  const sortData = useCallback((data, sortBy, orderBy) => {
    if (!data || data.length === 0) return data;
    if (sortBy === "Sort By" || orderBy === "Order By") return data;

    const sortedData = [...data];
    const isHigherToLower = orderBy === "Higher to Lower";

    try {
      if (sortBy === "By Price") {
        sortedData.sort((a, b) => {
          const aPrice = extractPriceValue(a.price);
          const bPrice = extractPriceValue(b.price);
          
          if (aPrice === 0 && bPrice === 0) return 0;
          if (aPrice === 0) return 1;
          if (bPrice === 0) return -1;
          
          return isHigherToLower ? bPrice - aPrice : aPrice - bPrice;
        });
      } else if (sortBy === "By Area") {
        sortedData.sort((a, b) => {
          const aArea = extractArea(a.land_area);
          const bArea = extractArea(b.land_area);
          
          if (aArea === 0 && bArea === 0) return 0;
          if (aArea === 0) return 1;
          if (bArea === 0) return -1;
          
          return isHigherToLower ? bArea - aArea : aArea - bArea;
        });
      } else if (sortBy === "By Floor Area") {
        sortedData.sort((a, b) => {
          const aFloorArea = extractFloorArea(a.total_area);
          const bFloorArea = extractFloorArea(b.total_area);
          
          if (aFloorArea === 0 && bFloorArea === 0) return 0;
          if (aFloorArea === 0) return 1;
          if (bFloorArea === 0) return -1;
          
          return isHigherToLower ? bFloorArea - aFloorArea : aFloorArea - bFloorArea;
        });
      } else if (sortBy === "By Year") {
        sortedData.sort((a, b) => {
          const aYear = extractYear(a.built);
          const bYear = extractYear(b.built);
          
          if (aYear === 0 && bYear === 0) return 0;
          if (aYear === 0) return 1;
          if (bYear === 0) return -1;
          
          return isHigherToLower ? bYear - aYear : aYear - bYear;
        });
      }
    } catch (error) {
      console.error("Error sorting kominka data:", error);
      return data;
    }

    return sortedData;
  }, [extractPriceValue, extractArea, extractFloorArea, extractYear]);

  // Process and sort data
  useEffect(() => {
    if (akiyaData && akiyaData.length > 0) {
      setIsLoading(true);
      try {
        const sortedData = sortData(akiyaData, sortBy, orderBy);
        setViewData(sortedData);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error processing kominka data:", error);
        setViewData(akiyaData);
      } finally {
        setIsLoading(false);
      }
    }
  }, [akiyaData, sortBy, orderBy, sortData]);

  // Memoized pagination data
  const paginationData = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = viewData.slice(indexOfFirstItem, indexOfLastItem);
    
    return {
      currentItems,
      totalItems: viewData.length,
      totalPages: Math.ceil(viewData.length / itemsPerPage)
    };
  }, [viewData, currentPage, itemsPerPage]);

  // Page change handler
  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    setSortBy("Sort By");
    setOrderBy("Order By");
    setCurrentPage(1);
  }, []);

  // Loading state
  if (!akiyaData) {
    return (
      <div className="flex justify-center gap-3 my-2rem">
        <div className="max-w-[1440px] lg:mx-3rem md:mx-2rem mx-1rem w-[97%] flex flex-col">
          <Loader />
        </div>
      </div>
    );
  }

  // No data state
  if (akiyaData.length === 0) {
    return (
      <div className="flex justify-center gap-3 my-2rem">
        <div className="max-w-[1440px] lg:mx-3rem md:mx-2rem mx-1rem w-[97%] flex flex-col">
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Kominka Properties Found</h3>
            <p className="text-gray-500">Check back later for new traditional Japanese house listings.</p>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = sortBy !== "Sort By" || orderBy !== "Order By";

  return (
    <div className="flex justify-center gap-3 my-2rem">
      <div className="max-w-[1440px] lg:mx-3rem md:mx-2rem mx-1rem w-[97%] flex flex-col">
        <div>
          <h1 className="mt-3 text-[2rem] font-bold text-left text-[#333333]">
            Kominka Listing
          </h1>
          
          {/* Map Section */}
          <div className="map mb-6 h-[30rem] bg-gray-200 w-full rounded-xl overflow-hidden shadow-lg">
            <MapComponent
              akiyaData={akiyaData}
              prefectureName={prefecture_name}
              apiKey={"AIzaSyDhd_bCluPIx2cSHS7pGd5wVPkIRU7rpxQ&"}
            />
          </div>

          <div className="w-full">
            {/* Enhanced Controls Section with Kominka Orange Theme */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                
                {/* Left side - Filters */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  <h3 className="text-lg font-semibold text-gray-900 whitespace-nowrap">
                    Filter & Sort:
                  </h3>
                  
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    {sortDropDownOptions && (
                      <SortDropDown
                        options={sortDropDownOptions}
                        text={sortBy}
                        handleChange={handleSortbyChange}
                        color={{ primary: "#e09321" }} // Kominka orange theme
                      />
                    )}
                    
                    {sortingType && (
                      <SortDropDown
                        options={sortingType}
                        text={orderBy}
                        handleChange={handleOrderByChange}
                        color={{ primary: "#e09321" }}
                        disabled={sortBy === "Sort By"}
                      />
                    )}
                    
                    {/* Reset button */}
                    {hasActiveFilters && (
                      <button
                        onClick={handleResetFilters}
                        className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reset
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Right side - Favorites button with Kominka styling */}
                <button
                  onClick={handleFavoriteClick}
                  className="group flex items-center gap-2 px-4 sm:px-6 py-2.5 bg-[#e09321] hover:bg-[#c7811d] text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                >
                  <svg className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  <span className="hidden sm:inline">Your Favorites</span>
                  <span className="sm:hidden">Favorites</span>
                </button>
              </div>
            </div>

            {/* Results Summary */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-2">
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{viewData.length}</span> traditional houses found
                {hasActiveFilters && (
                  <span className="ml-1">
                    • Sorted by <span className="font-medium">{sortBy.replace("By ", "")}</span>
                    {orderBy !== "Order By" && (
                      <span> ({orderBy})</span>
                    )}
                  </span>
                )}
              </div>
              
              {isLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="w-4 h-4 border-2 border-[#e09321] border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </div>
              )}
            </div>

            {/* Properties Grid */}
            <div className="grid lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
              {paginationData.currentItems.map((item) => (
                <AkiyaCard 
                  key={item.post_id || item.id || Math.random()} 
                  item={item} 
                />
              ))}
            </div>

            {/* No results message */}
            {paginationData.currentItems.length === 0 && !isLoading && (
              <div className="text-center py-12 bg-orange-50 rounded-xl border border-orange-100">
                <svg className="w-12 h-12 text-orange-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No kominka properties found</h3>
                <p className="text-gray-500">Try adjusting your filters to see more traditional houses.</p>
              </div>
            )}

            {/* Enhanced Pagination with Kominka theme */}
            <Pagination
              currentPage={currentPage}
              totalItems={paginationData.totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              showInfo={true}
              maxPageNumbers={5}
              theme={{ primary: "#e09321" }} // Pass kominka orange theme
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Kominka;